import { PrismaMakers } from './PrismaMakers.js';
import { editImportedModel } from '../../lib/open-asset.js';
import { useCallback, useEffect, useState } from 'react';
import { Panel } from 'react-resizable-panels';

import type { EngineView } from '../../engine/prism.js';
import { ART_STYLES, modelRequest, type ArtStyle, type ModelAction } from '../../lib/ai-context.js';
import { useGeneration } from '../../state/generation.js';
import { askAssistant } from '../../lib/ask-ai.js';
import { partAt, resolvePartPath } from '../../lib/model-doc.js';
import { slugName } from './model-helpers.js';
import { ModelGenPanel } from './ModelGenPanel.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { useModel } from '../../state/model.js';
import { IconButton, isTyping, MenuButton } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { Handle, SplitGroup } from '../ui/layout.js';
import { PanelHeader, SaveIndicator } from '../scene/SceneTab.js';
import { promptText } from '../../lib/prompt-dialog.js';
import { Viewport } from '../Viewport.js';
import { ModelSchematic } from './ModelSchematic.js';
import { MaterialsEditor, PartInspector } from './PartInspector.js';
import { PartsTree } from './PartsTree.js';
import { TexturePresetsEditor } from './TexturePresets.js';
import { RigWorkbench } from './RigWorkbench.js';
import { matchMeshCommand } from './mesh-keymap.js';
import { meshApi, MeshToolbar, MeshViewportOverlay, overlayItems, shadingItems } from './MeshToolbar.js';
import { SculptToolbar } from './sculpt/SculptToolbar.js';
import { PaintModeButton, PaintToolbar } from './paint/PaintToolbar.js';
import { PaintLayersPanel } from './paint/PaintLayersPanel.js';
import { StencilOverlay } from './paint/StencilOverlay.js';
import { WEBSITE, LOCAL_AI } from '../../lib/edition.js';
import { requireLocalGen } from '../../state/local-gen.js';

function ModelPicker({ onNew }: { onNew: () => void }) {
  const entries = useProjectFiles((s) => s.entries);
  const path = useModel((s) => s.path);
  const models = filesOfKind(entries, 'model');
  const imported = filesOfKind(entries, 'mesh').filter((p) => p.toLowerCase().endsWith('.glb'));
  return (
    <>
      <MenuButton
        className="picker"
        title="Open model"
        items={() => [
          { id: 'h', label: 'Models', heading: true },
          ...models.map((p) => ({ id: p, label: p, icon: 'model' as const, run: () => void useModel.getState().open(p) })),
          ...(models.length === 0 ? [{ id: 'none', label: 'No *.model.json files', disabled: true }] : []),
          ...(imported.length
            ? [
                { id: 'h2', label: 'Edit imported model (.glb)', heading: true },
                ...imported.map((p) => ({ id: `glb:${p}`, label: p, icon: 'cube' as const, run: () => void editImportedModel(p) })),
              ]
            : []),
          { id: 'sep', label: '', separator: true },
          { id: 'new', label: 'New model…', icon: 'plus' as const, run: onNew },
        ]}
      >
        <Icon name="model" size={13} />
        <span className="picker-label">{path ? path.replace(/^models\//, '').replace(/\.model\.json$/, '') : 'Open model…'}</span>
        <Icon name="chevronDown" size={12} />
      </MenuButton>
      <button type="button" className="tb-btn" onClick={onNew} title="Create a new model file">
        <Icon name="plus" size={13} /> New
      </button>
    </>
  );
}

async function newModel() {
  const name = await promptText('Name of the new model', 'New Model');
  if (!name?.trim()) return;
  const file = `models/${slugName(name)}.model.json`;
  await useModel.getState().create(file, name.trim());
  useProjectFiles.getState().refreshSoon();
}

function ModelToolbar() {
  const wireframe = useModel((s) => s.wireframe);
  const shading = useModel((s) => s.shading);
  const grid = useModel((s) => s.grid);
  const saveState = useModel((s) => s.saveState);
  const canUndo = useModel((s) => s.past.length > 0);
  const canRedo = useModel((s) => s.future.length > 0);
  const tool = useModel((s) => s.tool);
  const selected = useModel((s) => s.selected);
  const mesh = useModel((s) => s.meshEdit);
  const sculpt = useModel((s) => s.sculpt);
  const selectedIsMesh = useModel((s) => {
    const index = s.doc && s.selected ? resolvePartPath(s.doc, s.selected) : null;
    const part = s.doc && index ? partAt(s.doc, index) : null;
    return part?.shape?.type === 'mesh';
  });
  const space = useModel((s) => s.space);
  const snap = useModel((s) => s.snap);
  const meshOptions = useModel((s) => s.meshOptions);
  const s = useModel.getState();
  return (
    <div className="viewport-toolbar">
      <div className="tb-group">
        <ModelPicker onNew={() => void newModel()} />
      </div>
      <div className="tb-group segmented">
        <IconButton icon="cursor" title="Select" shortcut="Q" active={tool === 'select'} onClick={() => s.setTool('select')} />
        <IconButton icon="move" title="Move part" shortcut="W" active={tool === 'move'} onClick={() => s.setTool('move')} />
        <IconButton icon="rotate" title="Rotate part" shortcut="E" active={tool === 'rotate'} onClick={() => s.setTool('rotate')} />
        <IconButton icon="scale" title="Scale part" shortcut="R" active={tool === 'scale'} onClick={() => s.setTool('scale')} />
      </div>
      <div className="tb-group segmented">
        <IconButton
          icon={space === 'world' ? 'globe' : 'local'}
          title={space === 'world' ? 'World space' : 'Local space'}
          shortcut="X"
          onClick={() => s.setSpace(space === 'world' ? 'local' : 'world')}
        />
        <IconButton icon="magnet" title="Snap (hold Ctrl to toggle while dragging)" shortcut="Ctrl+G" active={snap} onClick={() => s.setSnap(!snap)} />
      </div>
      {selected && !mesh.active && !sculpt.active && (
        <div className="tb-group">
          <button
            type="button"
            className="tb-btn"
            title="Sculpt this part with brushes (simple shapes become sculpts; meshes get a sculpt op)"
            onClick={() => s.setSculpt(true)}
          >
            <Icon name="pencil" size={13} /> Sculpt
          </button>
          <PaintModeButton />
          {selectedIsMesh ? (
            <button type="button" className="tb-btn" title="Edit vertices and faces of this cage" onClick={() => s.setMeshEdit(true)}>
              <Icon name="edit" size={13} /> Edit mesh <kbd>Tab</kbd>
            </button>
          ) : (
            <button
              type="button"
              className="tb-btn"
              title="Turn this part into an editable quad cage (keeps its ops, e.g. subsurf)"
              onClick={s.convertToMesh}
            >
              <Icon name="cube" size={13} /> Convert to mesh
            </button>
          )}
        </div>
      )}
      {mesh.active && (
        <div className="tb-group mesh-tools">
          <button type="button" className="tb-btn" title="Leave mesh edit (Esc / Tab)" onClick={() => s.setMeshEdit(false)}>
            <Icon name="check" size={13} /> Done <kbd>Tab</kbd>
          </button>
        </div>
      )}
      <div className="tb-group segmented">
        <IconButton icon="wireframe" title="Wireframe" shortcut="Shift+Z" active={wireframe} onClick={() => s.setWireframe(!wireframe)} />
        <IconButton icon="smooth" title="Smooth shading" active={shading === 'smooth'} onClick={() => s.setShading('smooth')} />
        <IconButton icon="flat" title="Flat shading" active={shading === 'flat'} onClick={() => s.setShading('flat')} />
        <IconButton icon="grid" title="Grid" active={grid} onClick={() => s.setGrid(!grid)} />
        <MenuButton className={`ibtn${meshOptions.xray || meshOptions.quad || meshOptions.faceOrientation ? ' is-active' : ''}`} items={() => overlayItems(useModel.getState().meshOptions)} title="Overlays: statistics, normals, face orientation, measurement, X-ray, quad view">
          <Icon name="layers" size={15} />
        </MenuButton>
        <MenuButton className={`ibtn${meshOptions.matcap ? ' is-active' : ''}`} items={() => shadingItems(useModel.getState().meshOptions)} title="Viewport shading: materials or a matcap">
          <Icon name="sphere" size={15} />
        </MenuButton>
      </div>
      <IconButton icon="focus" title="Frame selected" shortcut="F" onClick={s.frameSelected} />
      <span className="tb-spacer" />
      <div className="tb-group segmented">
        <IconButton icon="undo" title="Undo" shortcut="Ctrl+Z" disabled={!canUndo} onClick={s.undo} />
        <IconButton icon="redo" title="Redo" shortcut="Ctrl+Y" disabled={!canRedo} onClick={s.redo} />
      </div>
      <SaveIndicator state={saveState} />
    </div>
  );
}

const QUICK_ACTIONS: Array<{ action: ModelAction; label: string; icon: IconName; needsPart?: boolean }> = [
  { action: 'generate', label: 'Generate new model', icon: 'sparkles' },
  { action: 'refine', label: 'Refine selected part', icon: 'wand', needsPart: true },
  { action: 'detail', label: 'Add detail', icon: 'layers' },
  { action: 'lowpoly', label: 'Make low-poly', icon: 'flat' },
  { action: 'stylized', label: 'Make stylized', icon: 'material' },
];

/** The modeling prompt: free text plus one-click requests to the Assistant. */
function AiPromptBar() {
  const path = useModel((s) => s.path);
  const selected = useModel((s) => s.selected);
  const projectId = useProjectFiles((s) => s.projectId);
  const styleKey = `prism.model.artStyle.${projectId ?? 'none'}`;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [style, setStyleState] = useState<ArtStyle>('auto');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(styleKey) as ArtStyle | null;
      setStyleState(saved && ART_STYLES.some((s) => s.value === saved) ? saved : 'auto');
    } catch {
      setStyleState('auto');
    }
  }, [styleKey]);
  const setStyle = (s: ArtStyle) => {
    setStyleState(s);
    try {
      localStorage.setItem(styleKey, s);
    } catch {
      // Private mode: the choice lasts for this session only.
    }
  };
  // Local generation switches live in the Assistant window (Local generation settings).
  const modelGen = useGeneration((st) => st.settings.types.model);
  const [genOpen, setGenOpen] = useState(false);
  useEffect(() => {
    void useGeneration.getState().setProject(projectId);
  }, [projectId]);

  async function send(action: ModelAction) {
    if (busy) return;
    if (action === 'custom' && !text.trim()) return;
    setBusy(true);
    const ok = await askAssistant(
      modelRequest({ action, prompt: text, modelPath: path, partPath: selected, style, localGen: modelGen.allow && modelGen.only }),
    );
    setBusy(false);
    if (ok) setText('');
  }

  return (
    <div className="ai-prompt">
      <form
        className="ai-prompt-row"
        onSubmit={(e) => {
          e.preventDefault();
          void send(path ? 'custom' : 'generate');
        }}
      >
        <Icon name="sparkles" size={16} className="ai-glyph" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={path ? 'Describe what to model or change…' : 'Describe a model to generate…'}
          aria-label="Describe what to model or change"
        />
        <select className="ai-style" value={style} onChange={(e) => setStyle(e.target.value as ArtStyle)} aria-label="Art style" title="Art style the Assistant builds in">
          {ART_STYLES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        {selected && (
          <span className="ctx-chip ctx-part" title={`Selected part: ${selected}`}>
            <Icon name="layers" size={11} /> {selected}
          </span>
        )}
        <button type="submit" className="primary small" disabled={busy || !text.trim()}>
          <Icon name="send" size={13} />
        </button>
      </form>
      {genOpen && <ModelGenPanel prompt={text} openPath={path} projectId={projectId} />}
      <PrismaMakers modelPath={path} />
      <div className="ai-actions">
        {QUICK_ACTIONS.map((q) => (
          <button
            key={q.action}
            type="button"
            className="ai-action"
            disabled={busy || (q.needsPart && !selected) || (q.action !== 'generate' && !path)}
            onClick={() => void send(q.action)}
            title={q.needsPart && !selected ? 'Select a part first' : q.label}
          >
            <Icon name={q.icon} size={12} /> {q.label}
          </button>
        ))}
        {LOCAL_AI && (
        <button
          type="button"
          className={`ai-local-gen${genOpen ? ' is-open' : ''}`}
          aria-expanded={genOpen}
          onClick={() => (genOpen ? setGenOpen(false) : requireLocalGen() && setGenOpen(true))}
          title="Generate the mesh on this PC with a local generator or a chain of them (SDXL concept image, Hunyuan3D, TRELLIS.2...). Which kinds are allowed is set in the Assistant window."
        >
          <Icon name="local" size={12} /> Generate locally...
        </button>
        )}
      </div>
    </div>
  );
}

/** Blender-like, AI-first model editor for *.model.json. */
export function ModelTab({ active }: { active: boolean }) {
  const doc = useModel((s) => s.doc);
  const path = useModel((s) => s.path);
  const error = useModel((s) => s.error);
  const selected = useModel((s) => s.selected);
  const wireframe = useModel((s) => s.wireframe);
  const meshActive = useModel((s) => s.meshEdit.active);
  const entries = useProjectFiles((s) => s.entries);
  const [side, setSide] = useState<'part' | 'materials' | 'textures'>('part');
  const [rigEditing, setRigEditing] = useState(false);

  const onReady = useCallback(async (view: EngineView) => {
    const s = useModel.getState();
    view.setOption('wireframe', String(s.wireframe));
    view.setOption('shading', s.shading);
    view.setOption('grid', String(s.grid));
    view.setTool(s.tool);
    view.setOption('space', s.space);
    view.setOption('snap', String(s.snap));
    view.setOption('meshEdit', JSON.stringify(s.meshOptions));
    if (s.path) await view.openModel(s.path);
    if (s.selected) view.setOption('selection', s.selected);
  }, []);

  useEffect(() => {
    if (!active || rigEditing) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const s = useModel.getState();
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      // With the viewport focused the engine handles edit-mode keys itself;
      // only keep the browser from moving focus on Tab.
      const fromViewport = (e.target as HTMLElement | null)?.tagName === 'CANVAS';
      // Sculpt keys: components/model/sculpt/SculptToolbar.tsx (shortcut registry, `model.sculpt`);
      // in the viewport the engine handles the rest (Esc cancels a gesture first).
      if (s.sculpt.active && fromViewport) return;
      if (e.key === 'Tab' && !ctrl && !e.altKey && (s.meshEdit.active || s.selected)) {
        e.preventDefault();
        if (!fromViewport) s.setMeshEdit(!s.meshEdit.active);
        return;
      }
      if (s.meshEdit.active) {
        // Interactive tools own the keyboard in the viewport (Esc, Enter, E, F...).
        if (fromViewport && s.meshEdit.modal) return;
        const command = matchMeshCommand(e);
        if (command && !(fromViewport && command.engine)) {
          e.preventDefault();
          command.run(meshApi());
          return;
        }
        if (fromViewport && command) return;
      }
      if (ctrl && key === 'z' && !e.shiftKey) s.undo();
      else if (ctrl && (key === 'y' || (key === 'z' && e.shiftKey))) s.redo();
      else if (ctrl && key === 'd') s.duplicateSelected();
      else if (ctrl && key === 's') void s.flush();
      else if (ctrl && key === 'g') s.setSnap(!s.snap);
      else if (ctrl || e.altKey) return;
      else if (key === 'q') s.setTool('select');
      else if (key === 'w') s.setTool('move');
      else if (key === 'e') s.setTool('rotate');
      else if (key === 'r') s.setTool('scale');
      else if (key === 'x') s.setSpace(s.space === 'world' ? 'local' : 'world');
      else if (e.shiftKey && key === 'z') s.setWireframe(!s.wireframe);
      else if (key === 'f') s.frameSelected();
      else if (e.key === 'Delete' || e.key === 'Backspace') s.deleteSelected();
      else if (e.key === 'Escape') s.select(null);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, rigEditing]);

  const models = filesOfKind(entries, 'model');
  const imported = filesOfKind(entries, 'mesh').filter((p) => p.toLowerCase().endsWith('.glb'));

  return (
    <div className="tab-body tab-model">
      <div className="model-mode-bar" role="tablist" aria-label="Model workspace">
        <button type="button" role="tab" aria-selected={!rigEditing} className={!rigEditing ? 'is-active' : ''} onClick={() => setRigEditing(false)}>Model</button>
        <button type="button" role="tab" aria-selected={rigEditing} className={rigEditing ? 'is-active' : ''} onClick={() => setRigEditing(true)}>Rig & Animate</button>
      </div>
      {rigEditing ? <RigWorkbench /> : <SplitGroup id="model-h" orientation="horizontal" fallback={{ parts: 17, center: 60, props: 23 }}>
        <Panel id="parts" minSize={170} className="pane">
          <PanelHeader title="Parts" />
          {doc ? <PartsTree /> : <div className="pane-empty muted">No model open</div>}
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="center" minSize={320} className="pane pane-center">
          <ModelToolbar />
          {meshActive && <MeshToolbar />}
          <SculptToolbar />
          <PaintToolbar />
          <Viewport
            mode="model"
            onReady={onReady}
            fallback={doc ? <ModelSchematic doc={doc} selected={selected} wireframe={wireframe} onSelect={(p) => useModel.getState().select(p)} /> : undefined}
          >
            {!doc && (
              <div className="viewport-message">
                <Icon name="model" size={22} />
                <strong>{error ? 'Could not open the model' : 'Open or create a model'}</strong>
                {error && <span className="mono muted">{error}</span>}
                <div className="scene-list">
                  {models.slice(0, 6).map((p) => (
                    <button key={p} type="button" className="ghost small" onClick={() => void useModel.getState().open(p)}>
                      {p}
                    </button>
                  ))}
                  <button type="button" className="primary small" onClick={() => void newModel()}>
                    <Icon name="plus" size={13} /> New model
                  </button>
                </div>
                {imported.length > 0 && (
                  <>
                    <span className="muted">Edit an imported model (makes an editable copy; the .glb is kept):</span>
                    <div className="scene-list">
                      {imported.slice(0, 8).map((p) => (
                        <button key={p} type="button" className="ghost small" onClick={() => void editImportedModel(p)}>
                          <Icon name="cube" size={12} /> {p}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <span className="muted">…or describe one to the AI below.</span>
              </div>
            )}
            {path && <span className="viewport-caption mono">{path}</span>}
            <MeshViewportOverlay />
            <StencilOverlay />
            <PaintLayersPanel />
            <AiPromptBar />
          </Viewport>
        </Panel>
        <Handle orientation="vertical" />
        <Panel id="props" minSize={250} className="pane">
          <div className="subtabs panel-subtabs" role="tablist">
            <button type="button" role="tab" aria-selected={side === 'part'} className={`subtab${side === 'part' ? ' is-active' : ''}`} onClick={() => setSide('part')}>
              Part
            </button>
            <button type="button" role="tab" aria-selected={side === 'materials'} className={`subtab${side === 'materials' ? ' is-active' : ''}`} onClick={() => setSide('materials')}>
              Materials
            </button>
            <button type="button" role="tab" aria-selected={side === 'textures'} className={`subtab${side === 'textures' ? ' is-active' : ''}`} onClick={() => setSide('textures')}>
              Textures
            </button>
          </div>
          <div className="inspector">{side === 'part' ? <PartInspector /> : side === 'materials' ? <MaterialsEditor /> : <TexturePresetsEditor />}</div>
        </Panel>
      </SplitGroup>}
    </div>
  );
}
